import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, logInfo } from '../utils/logger';

export const AuthContext = createContext(null);

// Admin setup helper function
const setupAdminUser = async (userId) => {
  const now = new Date().toISOString();
  
  // Create admin subscription with all addons
  await setDoc(doc(db, 'subscriptions', userId), {
    status: 'active',
    plan: 'enterprise',
    isValid: true,
    addons: [
      'roadguard',
      'cargocare', 
      'permitpro',
      'analytics',
      'api_access',
      'custom_integrations'
    ],
    expiryDate: '2035-12-31T00:00:00.000Z',
    autoRenew: true,
    createdAt: now,
    updatedAt: now
  }, { merge: true });

  // Create admin profile
  await setDoc(doc(db, 'userProfiles', userId), {
    userId,
    email: 'jfprince@soltec.ca',
    role: 'admin', // Make sure this is 'admin'
    emailVerified: true,
    name: 'System Administrator',
    permissions: {
      canEdit: true,
      canDelete: true,
      canExport: true,
      canInvite: true,
      canManageUsers: true,
      canManageRoles: true,
      canManageSubscriptions: true,
      canAccessAdmin: true,
      canManageSettings: true
    },
    createdAt: now,
    updatedAt: now
  }, { merge: true });

  // Create admin settings
  await setDoc(doc(db, 'userSettings', userId), {
    preferences: {
      language: 'en',
      notifications: { enabled: true, email: true },
      autoLogin: true,
      adminFeatures: {
        showAllSurveys: true,
        showAllUsers: true,
        showAnalytics: true,
        showSystemSettings: true
      },
      menuLayout: {
        order: ['admin', 'roadscope', 'roadguard', 'cargocare', 'permitpro', 'settings'],
        visibility: {
          admin: true,
          roadscope: true,
          roadguard: true,
          cargocare: true,
          permitpro: true,
          settings: true
        }
      }
    },
    createdAt: now,
    updatedAt: now
  }, { merge: true });

  // Create admin subscription
  await setDoc(doc(db, 'subscriptions', userId), {
    status: 'active',
    plan: 'enterprise',
    isValid: true,
    addons: [
      'roadguard',
      'cargocare',
      'permitpro',
      'analytics',
      'api_access',
      'custom_integrations'
    ],
    expiryDate: '2035-12-31T00:00:00.000Z',
    autoRenew: true,
    createdAt: now,
    updatedAt: now
  }, { merge: true });

  logInfo('Admin user setup completed');
  return true;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  // Admin emails list
  const ADMIN_EMAILS = [
    'jfprince@soltec.ca',
    'jfprince29@gmail.com', 
    'admin@oversizehub.net'
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        logInfo('[AuthContext] Auth state changed:', firebaseUser?.email);
        if (firebaseUser) {
          // Check if user is admin by email
          const isAdminEmail = ADMIN_EMAILS.includes(firebaseUser.email?.toLowerCase());
          logInfo('[AuthContext] Is admin email:', isAdminEmail);

          const userRef = doc(db, 'userProfiles', firebaseUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Get user settings for menu layout
            const userSettingsRef = doc(db, 'userSettings', firebaseUser.uid);
            const userSettingsDoc = await getDoc(userSettingsRef);
            const userSettings = userSettingsDoc.exists() ? userSettingsDoc.data() : null;

            // Determine admin status
            const isAdmin = isAdminEmail || userData.role === 'admin';
            logInfo('[AuthContext] User role:', userData.role, 'Is admin:', isAdmin);
            const menuLayout = userSettings?.preferences?.menuLayout || {
              order: isAdmin ? 
                ['admin', 'roadscope', 'roadguard', 'cargocare', 'permitpro', 'settings'] :
                ['roadscope', 'roadguard', 'cargocare', 'permitpro', 'settings'],
              visibility: {
                admin: isAdmin,
                roadscope: true,
                roadguard: true,
                cargocare: true,
                permitpro: true,
                settings: true
              }
            };

            setUser({
              ...firebaseUser,
              ...userData,
              role: isAdmin ? 'admin' : userData.role,
              isAdmin,
              menuLayout
            });
            logInfo(`User ${isAdmin ? 'has' : 'does not have'} admin access`);
          } else if (ADMIN_EMAILS.includes(firebaseUser.email?.toLowerCase())) {
            // Create admin profile if it doesn't exist
            await setupAdminUser(firebaseUser.uid);
            setUser({
              ...firebaseUser,
              role: 'admin',
              isAdmin: true,
              menuLayout: {
                order: ['admin', 'roadscope', 'roadguard', 'cargocare', 'permitpro', 'settings'],
                visibility: {
                  admin: true,
                  roadscope: true,
                  roadguard: true,
                  cargocare: true,
                  permitpro: true,
                  settings: true
                }
              }
            });
            logInfo('Admin user created and authenticated');
          }
        } else {
          setUser(null);
          logInfo('No authenticated user');
        }
      } catch (error) {
        logError('Auth state change error:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};